import { Controller } from '@hotwired/stimulus';

/** Identifies the Zendesk loader among the page's script loads. */
const SNIPPET_PATH = 'static.zdassets.com/ekr/snippet.js';
/** How often to check whether Intercom has booted. */
const INTERCOM_POLL_MS = 100;

/**
 * Messenger state, shared at module level because the footer link and a Care
 * card can sit on the same page.
 */
let listening = false;
let messengerOpen = false;
/** Controller whose click is waiting for the messenger's open event. */
let pendingCareClick = null;
/** Reverts the last clicked link to email if the messenger never appears. */
let openTimer = null;

/** Records the pending click and stops the revert once the messenger opens. */
const onMessengerOpen = () => {
  messengerOpen = true;
  clearTimeout(openTimer);
  if (pendingCareClick) {
    pendingCareClick.upgrade();
    pendingCareClick.recordCareClick(pendingCareClick.providerValue);
    pendingCareClick = null;
  }
};

const onMessengerClose = () => {
  messengerOpen = false;
};

/**
 * The calls that differ between chat products. Everything else (the email-first
 * upgrade, the revert timer, one channel per click) lives in the controller, so
 * it holds for both.
 */
const PROVIDERS = {
  /** Messaging, so the API is zE('messenger', …). See shared/_zendesk_widget.html.erb. */
  zendesk: {
    loaded: () => typeof window.zE === 'function',

    /**
     * zE is a queueing stub from the moment the loader runs, so its presence
     * proves nothing. Zendesk defers the config callback until the messenger
     * has loaded its configuration.
     */
    whenReady: (callback) => {
      window.zE('messenger:get', 'config', callback);
      return () => {};
    },

    listen: () => {
      window.zE('messenger:on', 'open', onMessengerOpen);
      window.zE('messenger:on', 'close', onMessengerClose);
    },

    /**
     * Hands Care the "Cancel context" ticket field, which Zendesk applies only
     * to a new conversation, then always shows and opens: Zendesk restores an
     * open messenger on reload, but the partial hides it.
     */
    open: (controller) => {
      if (controller.fieldsValue.length) window.zE('messenger:set', 'conversationFields', controller.fieldsValue);
      window.zE('messenger', 'show');
      window.zE('messenger', 'open');
    },
  },

  intercom: {
    loaded: () => typeof window.Intercom === 'function',

    /**
     * Intercom documents no boot-complete callback. Its loader sets booted on
     * the queue as it starts the Messenger frame, and the frame replays the
     * queue, so an early click still opens chat. A blocked loader never sets it.
     */
    whenReady: (callback) => {
      const timer = setInterval(() => {
        if (window.Intercom?.booted !== true) return;
        clearInterval(timer);
        callback();
      }, INTERCOM_POLL_MS);
      return () => clearInterval(timer);
    },

    listen: () => {
      window.Intercom('onShow', onMessengerOpen);
      window.Intercom('onHide', onMessengerClose);
    },

    /**
     * Sends the cancel context as an event and as customer attributes before
     * showing, so Care sees it while the customer's message box starts empty.
     */
    open: (controller) => {
      window.Intercom('trackEvent', 'cancel_care_requested', controller.contextValue);
      window.Intercom('update', controller.attributesValue);
      window.Intercom('show');
    },
  },
};

/**
 * Upgrades a Care email link to chat once the messenger is ready.
 *
 * The server always renders the email version: a real mailto with email copy.
 * That is what the customer gets with no JavaScript, no consent, an ad blocker,
 * or a failed load. Only when the messenger is ready does this swap in the chat
 * label and note and take over the click. After that a click opens chat only,
 * never the mail client as well. If the messenger does not appear in time, the
 * link goes back to email for the customer's next click.
 */
export default class extends Controller {
  static targets = ['link', 'note'];
  static values = {
    provider: { type: String, default: 'zendesk' },
    label: String,
    note: String,
    fields: Array,
    context: Object,
    attributes: Object,
    clickUrl: String,
    clickPage: String,
    clickOrderIssueSubReason: String,
    openTimeout: { type: Number, default: 10000 },
  };

  /**
   * Waits for the messenger now if its loader already ran, or if it is
   * Intercom, whose wait covers the load too. Otherwise waits for the Zendesk
   * snippet, listening in the capture phase because load does not bubble. That
   * also catches a load that Cookiebot releases after consent.
   */
  connect() {
    if (this.providerValue === 'intercom' || this.provider.loaded()) {
      this.awaitMessenger();
      return;
    }

    this.onLoad = (event) => {
      if (String(event.target && event.target.src).includes(SNIPPET_PATH)) this.awaitMessenger();
    };
    document.addEventListener('load', this.onLoad, true);
  }

  /** Drops the load listener and any pending readiness check. */
  disconnect() {
    this.stopWaiting();
  }

  /** @returns {Object} the PROVIDERS entry for this page's chat product */
  get provider() {
    return PROVIDERS[this.providerValue] || PROVIDERS.zendesk;
  }

  /** Upgrades once the messenger is ready. If it never is, the link stays email. */
  awaitMessenger() {
    this.stopWaiting();
    this.cancelReady = this.provider.whenReady(() => this.upgrade());
  }

  /**
   * Opens the messenger. Before the upgrade the link is still email, so the
   * click goes through to the mailto. Only a closed messenger gets the revert
   * timer, because showing an open one fires no event to cancel it.
   *
   * @param {MouseEvent} event the click on the Care link
   */
  open(event) {
    if (!this.upgraded) {
      this.recordCareClick('mailto');
      return;
    }

    event.preventDefault();
    if (messengerOpen) {
      this.recordCareClick(this.providerValue);
    } else {
      pendingCareClick = this;
      clearTimeout(openTimer);
      openTimer = setTimeout(() => this.downgrade(), this.openTimeoutValue);
    }

    try {
      this.provider.open(this);
    } catch {
      clearTimeout(openTimer);
      this.emailCare();
    }
  }

  /**
   * Swaps the email copy for chat copy, once the messenger is ready. Listens
   * first, so a throw leaves the link as a plain mailto.
   */
  upgrade() {
    if (this.upgraded || !this.provider.loaded()) return;

    this.listen();
    this.upgraded = true;
    if (this.hasLinkTarget) {
      this.emailLabel = this.linkTarget.textContent;
      if (this.labelValue) this.linkTarget.textContent = this.labelValue;
    }
    if (this.hasNoteTarget) {
      this.emailNote = this.noteTarget.textContent;
      if (this.noteValue) this.noteTarget.textContent = this.noteValue;
    }
  }

  /**
   * Puts the email copy back and hands the next click to the mailto. Never
   * navigates by itself, so the customer cannot get mail and chat at once.
   * The click stays pending, so a late open can still claim it.
   */
  downgrade() {
    this.upgraded = false;
    if (this.hasLinkTarget && this.emailLabel !== undefined) this.linkTarget.textContent = this.emailLabel;
    if (this.hasNoteTarget && this.emailNote !== undefined) this.noteTarget.textContent = this.emailNote;
  }

  /** Subscribes to the messenger's open and close events, once per page. */
  listen() {
    if (listening) return;

    this.provider.listen();
    listening = true;
  }

  /**
   * Follows the mailto when a provider call throws. The messenger did not
   * open, so the customer gets one channel only.
   */
  emailCare() {
    pendingCareClick = null;
    this.recordCareClick('mailto');
    window.location.href = this.linkTarget.href;
  }

  /**
   * Fire-and-forget cancel funnel Care click for PostHog (server-side capture).
   *
   * @param {'zendesk' | 'intercom' | 'mailto'} channel
   */
  recordCareClick(channel) {
    if (!this.hasClickUrlValue) return;

    const body = new FormData();
    body.append('channel', channel);
    if (this.clickPageValue) body.append('page', this.clickPageValue);
    if (this.clickOrderIssueSubReasonValue) {
      body.append('order_issue_sub_reason', this.clickOrderIssueSubReasonValue);
    }

    fetch(this.clickUrlValue, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        Accept: 'application/json',
      },
      body,
    }).catch(() => {});
  }

  /** Removes the snippet load listener and the readiness check, if either is waiting. */
  stopWaiting() {
    if (this.onLoad) document.removeEventListener('load', this.onLoad, true);
    this.onLoad = null;
    this.cancelReady?.();
    this.cancelReady = null;
  }
}
